'use client'

import * as React from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Filter, Calendar, DollarSign, Building2, MoreHorizontal, ExternalLink } from 'lucide-react'
import { addDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Progress } from 'components/ui/progress'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { cn } from 'lib/utils'
import { toast } from 'sonner'
import { db } from '../../../firebase'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithAward } from 'lib/access'

const statusColors= {
  active: 'bg-success/15 text-success border-success/30',
  closed: 'bg-muted text-muted-foreground border-border',
  pending: 'bg-warning/15 text-warning border-warning/30',
}

export default function AwardsPage() {
  const { user, accessLevel, canEdit } = useUser()
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('all')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [awards, setAwards] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [users, setUsers] = React.useState([])
  const [proposals, setProposals] = React.useState([])
  const [stakeholders, setStakeholders] = React.useState([])
  const [formData, setFormData] = React.useState({
    title: '',
    donorName: '',
    donorId: '',
    grantNumber: '',
    startDate: '',
    endDate: '',
    totalBudget: '',
    currency: 'USD',
    proposalId: '',
    members: '',
    principalInvestigator: '',
    coPrincipalInvestigator: '',
    projectMembers: [],
  })

  const normalizeDate = (value) => {
    if (!value) return null
    if (value?.toDate) return value.toDate()
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  React.useEffect(() => {
    let isMounted = true

    async function loadAwards() {
      try {
        const snapshot = await getDocs(collection(db, 'awards'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setAwards(records)
      } catch {
        setAwards([])
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadAwards()

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    let isMounted = true

    async function loadUsers() {
      try {
        const snapshot = await getDocs(collection(db, 'users'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setUsers(records)
      } catch {
        setUsers([])
      }
    }

    loadUsers()

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    let isMounted = true

    async function loadProposals() {
      try {
        const snapshot = await getDocs(collection(db, 'proposals'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setProposals(records)
      } catch {
        setProposals([])
      }
    }

    loadProposals()

    return () => {
      isMounted = false
    }
  }, [])

  React.useEffect(() => {
    let isMounted = true

    async function loadStakeholders() {
      try {
        const snapshot = await getDocs(collection(db, 'stakeholders'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setStakeholders(records)
      } catch {
        setStakeholders([])
      }
    }

    loadStakeholders()

    return () => {
      isMounted = false
    }
  }, [])

  const getUserLabel = (user) => {
    return user.fullName || user.name || user.email || 'User'
  }

  const limited = accessLevel === 'limited'
  const currentUserId = getUserId(user)
  const visibleAwards = limited
    ? awards.filter((award) => isUserAssociatedWithAward(award, currentUserId))
    : awards

  const filteredAwards = visibleAwards.filter((award) => {
    const matchesSearch =
      (award.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (award.donorName || '').toLowerCase().includes(search.toLowerCase()) ||
      (award.grantNumber || '').toLowerCase().includes(search.toLowerCase())
    const matchesStatus = statusFilter === 'all' || award.status === statusFilter
    return matchesSearch && matchesStatus
  })

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            Grant / Award Administration
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage donor-funded grants, awards, and their reporting obligations
          </p>
        </div>
        {canEdit ? (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-accent gap-2">
                <Plus className="size-4" />
                New Award
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif">Create New Award</DialogTitle>
              <DialogDescription>
                Register a new donor-funded grant or award
              </DialogDescription>
            </DialogHeader>
            <form
              className="flex flex-col gap-4 mt-4"
              onSubmit={async (e) => {
                e.preventDefault()
                if (!formData.title || !formData.donorName || !formData.grantNumber) {
                  toast.error('Please fill all required fields.')
                  return
                }
                setIsSubmitting(true)
                try {
                  const members = formData.members
                    .split(',')
                    .map((member) => member.trim())
                    .filter(Boolean)
                  const principalInvestigator = users.find(
                    (user) => (user.userId || user.id) === formData.principalInvestigator,
                  )
                  const coPrincipalInvestigator = users.find(
                    (user) => (user.userId || user.id) === formData.coPrincipalInvestigator,
                  )
                  const projectMembers = users
                    .filter((user) => formData.projectMembers.includes(user.userId || user.id))
                    .map((user) => ({
                      userId: user.userId || user.id,
                      fullName: getUserLabel(user),
                      email: user.email || '',
                    }))
                  const payload = {
                    title: formData.title.trim(),
                    donorName: formData.donorName.trim(),
                    donorId: formData.donorId || '',
                    grantNumber: formData.grantNumber.trim(),
                    startDate: formData.startDate || null,
                    endDate: formData.endDate || null,
                    totalBudget: Number(formData.totalBudget) || 0,
                    currency: formData.currency,
                    proposalId: formData.proposalId || '',
                    proposalTitle: proposals.find((proposal) => proposal.id === formData.proposalId)?.title || '',
                    members,
                    principalInvestigator: principalInvestigator
                      ? {
                          userId: principalInvestigator.userId || principalInvestigator.id,
                          fullName: getUserLabel(principalInvestigator),
                          email: principalInvestigator.email || '',
                        }
                      : null,
                    coPrincipalInvestigator: coPrincipalInvestigator
                      ? {
                          userId: coPrincipalInvestigator.userId || coPrincipalInvestigator.id,
                          fullName: getUserLabel(coPrincipalInvestigator),
                          email: coPrincipalInvestigator.email || '',
                        }
                      : null,
                    projectMembers,
                    status: 'pending',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                  }
                  const docRef = await addDoc(collection(db, 'awards'), payload)
                  setAwards((prev) => [
                    {
                      id: docRef.id,
                      implementingUnits: [],
                      ...payload,
                    },
                    ...prev,
                  ])
                  toast.success('Award created successfully')
                  setFormData({
                    title: '',
                    donorName: '',
                    donorId: '',
                    grantNumber: '',
                    startDate: '',
                    endDate: '',
                    totalBudget: '',
                    currency: 'USD',
                    proposalId: '',
                    members: '',
                    principalInvestigator: '',
                    coPrincipalInvestigator: '',
                    projectMembers: [],
                  })
                  setDialogOpen(false)
                } catch (error) {
                  toast.error('Failed to create award', {
                    description: error?.message || 'Please try again.',
                  })
                } finally {
                  setIsSubmitting(false)
                }
              }}
            >
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Linked Proposal</Label>
                <Select
                  value={formData.proposalId}
                  onValueChange={(value) => {
                    const proposal = proposals.find((item) => item.id === value)
                    setFormData((prev) => ({
                      ...prev,
                      proposalId: value,
                      title: prev.title || proposal?.title || '',
                      donorName: prev.donorName || proposal?.donorName || '',
                      totalBudget: prev.totalBudget || String(proposal?.totalBudget || ''),
                    }))
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={proposals.length === 0 ? 'No proposals available' : 'Select proposal'} />
                  </SelectTrigger>
                  <SelectContent>
                    {proposals.length === 0 ? (
                      <SelectItem value="no-proposals" disabled>
                        No proposals found
                      </SelectItem>
                    ) : (
                      proposals.map((proposal) => (
                        <SelectItem key={proposal.id} value={proposal.id}>
                          {proposal.title || proposal.id}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Award title</Label>
                <Input
                  placeholder="e.g., Climate-Smart Agriculture Programme"
                  className="h-9"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Donor / Funding Agency</Label>
                <Select
                  value={formData.donorId}
                  onValueChange={(value) => {
                    const stakeholder = stakeholders.find((item) => item.id === value)
                    setFormData((prev) => ({
                      ...prev,
                      donorId: value,
                      donorName: stakeholder?.organization || stakeholder?.name || stakeholder?.fullName || prev.donorName,
                    }))
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder={stakeholders.length === 0 ? 'No stakeholders available' : 'Select stakeholder'} />
                  </SelectTrigger>
                  <SelectContent>
                    {stakeholders.length === 0 ? (
                      <SelectItem value="no-stakeholders" disabled>
                        No stakeholders found
                      </SelectItem>
                    ) : (
                      stakeholders.map((stakeholder) => (
                        <SelectItem key={stakeholder.id} value={stakeholder.id}>
                          {stakeholder.name || stakeholder.fullName || 'Stakeholder'}
                          {stakeholder.organization ? ` • ${stakeholder.organization}` : ''}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Grant number</Label>
                <Input
                  placeholder="e.g., USAID-KE-2024-001"
                  className="h-9"
                  value={formData.grantNumber}
                  onChange={(e) => setFormData((prev) => ({ ...prev, grantNumber: e.target.value }))}
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Award members (organisation)</Label>
                <Input
                  placeholder="e.g., Dr. Achieng, Prof. Otieno"
                  className="h-9"
                  value={formData.members}
                  onChange={(e) => setFormData((prev) => ({ ...prev, members: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Principal investigator</Label>
                  <Select
                    value={formData.principalInvestigator}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, principalInvestigator: value }))
                    }
                  >
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select PI" /></SelectTrigger>
                    <SelectContent>
                      {users.length === 0 ? (
                        <SelectItem value="no-users" disabled>
                          No users available
                        </SelectItem>
                      ) : (
                        users.map((user) => (
                          <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                            {getUserLabel(user)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Co-principal investigator</Label>
                  <Select
                    value={formData.coPrincipalInvestigator}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, coPrincipalInvestigator: value }))
                    }
                  >
                    <SelectTrigger className="h-9"><SelectValue placeholder="Select Co-PI" /></SelectTrigger>
                    <SelectContent>
                      {users.length === 0 ? (
                        <SelectItem value="no-users" disabled>
                          No users available
                        </SelectItem>
                      ) : (
                        users.map((user) => (
                          <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                            {getUserLabel(user)}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-sm font-medium text-foreground">Project members</Label>
                <div className="max-h-40 overflow-auto rounded-md border border-border p-2">
                  {users.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No users available.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {users.map((user) => {
                        const value = user.userId || user.id
                        const checked = formData.projectMembers.includes(value)
                        return (
                          <label key={value} className="flex items-center gap-2 text-xs text-foreground">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(event) => {
                                const isChecked = event.target.checked
                                setFormData((prev) => ({
                                  ...prev,
                                  projectMembers: isChecked
                                    ? [...prev.projectMembers, value]
                                    : prev.projectMembers.filter((id) => id !== value),
                                }))
                              }}
                            />
                            {getUserLabel(user)}
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Start date</Label>
                  <Input
                    type="date"
                    className="h-9"
                    value={formData.startDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">End date</Label>
                  <Input
                    type="date"
                    className="h-9"
                    value={formData.endDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Total budget</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    className="h-9"
                    value={formData.totalBudget}
                    onChange={(e) => setFormData((prev) => ({ ...prev, totalBudget: e.target.value }))}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className="text-sm font-medium text-foreground">Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, currency: value }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="KES">KES</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" className="mt-2 bg-primary text-primary-foreground hover:bg-accent" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Award'}
              </Button>
            </form>
            </DialogContent>
          </Dialog>
        ) : null}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search awards, donors, grant numbers..."
            className="pl-10 h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-9">
            <Filter className="mr-2 size-3.5" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Awards Grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {isLoading ? (
          <Card className="border border-border">
            <CardContent className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              Loading awards...
            </CardContent>
          </Card>
        ) : filteredAwards.length === 0 ? (
          <Card className="border border-border">
            <CardContent className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              No awards found.
            </CardContent>
          </Card>
        ) : (
          filteredAwards.map((award) => {
          const startDate = normalizeDate(award.startDate)
          const endDate = normalizeDate(award.endDate)
          const now = new Date()
          const totalDuration = startDate && endDate ? endDate.getTime() - startDate.getTime() : 0
          const elapsed = startDate ? now.getTime() - startDate.getTime() : 0
          const timeProgress = totalDuration > 0
            ? Math.min(100, Math.max(0, (elapsed / totalDuration) * 100))
            : 0

          return (
            <Link key={award.id} to={`/dashboard/awards/${award.id}`} className="block">
            <Card className="border border-border hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col gap-1">
                    <CardTitle className="text-sm font-semibold text-foreground leading-tight">
                      {award.title}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {award.grantNumber}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={cn('text-[10px] border', statusColors[award.status])}>
                      {award.status}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="size-7">
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <ExternalLink className="mr-2 size-3.5" />
                          View Details
                        </DropdownMenuItem>
                        {canEdit ? (
                          <>
                            <DropdownMenuItem>Edit Award</DropdownMenuItem>
                            <DropdownMenuItem>Manage Partners</DropdownMenuItem>
                            <DropdownMenuItem>Documents</DropdownMenuItem>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="size-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{award.donorName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="size-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {award.currency} {(award.totalBudget / 1000000).toFixed(1)}M
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="size-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {endDate
                        ? endDate.toLocaleDateString('en-KE', { month: 'short', year: 'numeric' })
                        : '—'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Timeline progress</span>
                    <span className="text-[10px] font-medium text-foreground">
                      {Math.round(timeProgress)}%
                    </span>
                  </div>
                  <Progress value={timeProgress} className="h-1.5" />
                </div>
                <div className="flex flex-wrap gap-1">
                  {(award.implementingUnits || []).map((unit) => (
                    <Badge key={unit} variant="secondary" className="text-[10px]">
                      {unit}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
            </Link>
          )
        }))}
      </div>
    </div>
  )
}
